import React, {FC, ReactNode, useRef} from 'react'
import {useLinkedSettingsArrowOffset} from 'src/hooks/useLinkedSettingsArrowOffset'
import {getSequenceFieldWrapperClass} from 'src/utils/channelSettingSequenceLayout'
import {ChannelSettingLinkedPanelLayoutProvider} from './ChannelSettingLinkedPanelLayoutContext'

type LinkedSettingsChildPanelProps = {
  children: ReactNode
  className?: string
  anchorRef: React.Ref<HTMLElement>
}

/** Child panel with a speech-bubble arrow aligned under the parent control. */
export const LinkedSettingsChildPanel: FC<LinkedSettingsChildPanelProps> = ({
  children,
  className = 'settings-child-panel',
  anchorRef,
}) => {
  const panelRef = useRef<HTMLDivElement>(null)
  useLinkedSettingsArrowOffset(true, anchorRef, panelRef)

  return (
    <div ref={panelRef} className={className}>
      {children}
    </div>
  )
}

export type LinkedBooleanMappingBlockProps = {
  parentControl: ReactNode
  children: ReactNode
  isParentEnabled: boolean
  panelClassName?: string
  /** When the main sequence row splits this block, render only toggle or panel. */
  sequencePresentationPart?: 'toggle' | 'panel'
  /** Shared anchor for arrow alignment when toggle and panel are separate sequence slots. */
  sharedToggleAnchorRef?: React.Ref<HTMLDivElement>
}

const renderToggleColumn = (parentControl: ReactNode, anchorRef: React.Ref<HTMLDivElement>) => (
  <div
    ref={anchorRef}
    className={`boolean-setting-col ${getSequenceFieldWrapperClass('col-12 col-sm-6')}`.trim()}
  >
    <div className='settings-mapping-parent-toggle w-100'>{parentControl}</div>
  </div>
)

const renderPanelColumn = (
  props: Pick<
    LinkedBooleanMappingBlockProps,
    'children' | 'isParentEnabled' | 'panelClassName' | 'sharedToggleAnchorRef'
  >
) => {
  const {
    children,
    isParentEnabled,
    panelClassName = 'settings-mapping-child-panel',
    sharedToggleAnchorRef,
  } = props

  if (!isParentEnabled || !sharedToggleAnchorRef) {
    return null
  }

  const panelBody = (
    <ChannelSettingLinkedPanelLayoutProvider enabled={isParentEnabled}>
      {children}
    </ChannelSettingLinkedPanelLayoutProvider>
  )

  return (
    <div className='col-12'>
      <LinkedSettingsChildPanel className={panelClassName} anchorRef={sharedToggleAnchorRef}>
        {panelBody}
      </LinkedSettingsChildPanel>
    </div>
  )
}

/**
 * Boolean mapping parent + linked child panel.
 * Sequence row splits this via sequencePresentationPart (see expandSequencePresentationSlots).
 */
export const LinkedBooleanMappingBlock: FC<LinkedBooleanMappingBlockProps> = ({
  parentControl,
  children,
  isParentEnabled,
  panelClassName,
  sequencePresentationPart,
  sharedToggleAnchorRef,
}) => {
  const localAnchorRef = useRef<HTMLDivElement>(null)
  const anchorRef = sharedToggleAnchorRef ?? localAnchorRef

  if (sequencePresentationPart === 'toggle') {
    return renderToggleColumn(parentControl, anchorRef)
  }

  if (sequencePresentationPart === 'panel') {
    return renderPanelColumn({
      children,
      isParentEnabled,
      panelClassName,
      sharedToggleAnchorRef: anchorRef,
    })
  }

  return (
    <>
      {renderToggleColumn(parentControl, anchorRef)}
      {isParentEnabled
        ? renderPanelColumn({
            children,
            isParentEnabled,
            panelClassName,
            sharedToggleAnchorRef: anchorRef,
          })
        : null}
    </>
  )
}
